/**
 * Custom-protocol deep links (`<scheme>://...`) understood by the desktop app.
 *
 * Kept dependency-free so both the Electron main process and the Angular renderer can share the
 * same parser and URL builder.
 */
export type DeepLink = { kind: 'Call'; roomId: string } | { kind: 'Authenticate'; query: string };

export const DEEP_LINK_CALL_PATH = 'call';
export const DEEP_LINK_AUTHENTICATE_PATH = 'authenticate';

/** Call room ids are opaque, URL-safe tokens. Anything else is rejected before it reaches the router. */
const CALL_ROOM_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

export function isValidCallRoomId(roomId: string | null | undefined): roomId is string {
  return !!roomId && CALL_ROOM_ID_PATTERN.test(roomId);
}

export function buildProtocolCallUrl(protocol: string, roomId: string): string {
  return `${protocol}://${DEEP_LINK_CALL_PATH}/${encodeURIComponent(roomId)}`;
}

/**
 * Extracts the room id from a browser join URL (`<app base>/call/<id>`: prod/dev `/s/call/<id>`,
 * PR previews `/pr/<n>/app/call/<id>`, localhost `/call/<id>`). The host is deliberately not
 * checked; callers only pass URLs already identified as Spaces links, and an unknown room fails
 * the join with its own copy. `/calls/<id>` (the popped-out call window) is not a join URL.
 */
export function parseCallRoomJoinUrl(url: string | null | undefined): string | undefined {
  if (!url) {
    return undefined;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return undefined;
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return undefined;
  }

  const segments = parsed.pathname.split('/').filter(segment => segment.length > 0);
  if (segments.length < 2 || segments[segments.length - 2] !== DEEP_LINK_CALL_PATH) {
    return undefined;
  }

  let roomId: string;
  try {
    roomId = decodeURIComponent(segments[segments.length - 1]);
  } catch {
    return undefined;
  }

  return isValidCallRoomId(roomId) ? roomId : undefined;
}

/**
 * Parses a `<protocol>://...` URL into a typed deep link, or `undefined` when the URL is not for
 * this protocol or does not match a known shape.
 *
 * Accepts `://call/<id>`, `://call/<id>/`, `://authenticate?<query>` and `://authenticate/?<query>`.
 */
export function parseDeepLink(url: string, protocol: string): DeepLink | undefined {
  if (!url || !protocol) {
    return undefined;
  }

  const prefix = `${protocol}://`;
  if (!url.startsWith(prefix)) {
    return undefined;
  }

  const rest = url.slice(prefix.length);
  const queryIndex = rest.indexOf('?');
  const pathPart = queryIndex === -1 ? rest : rest.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : rest.slice(queryIndex + 1);
  const segments = pathPart.split('/').filter(segment => segment.length > 0);

  if (segments[0] === DEEP_LINK_AUTHENTICATE_PATH) {
    return query.length > 0 ? { kind: 'Authenticate', query } : undefined;
  }

  if (segments[0] === DEEP_LINK_CALL_PATH && segments.length === 2) {
    let roomId: string;
    try {
      roomId = decodeURIComponent(segments[1]);
    } catch {
      return undefined;
    }

    return isValidCallRoomId(roomId) ? { kind: 'Call', roomId } : undefined;
  }

  return undefined;
}
